import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Textarea, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import Loader from "@/components/Layouts/Loader";
import SelectShip from "@/components/Selects/SelectShip";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectInspector from "@/components/Selects/SelectInspector";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { cancelInspectionProcessSchema } from "@/schemas/cancelInspectionProcessSchema";
import { cancelNomination, getNominationById } from "@/services/nominationService";
import { getInspectorPersonData } from "@/services/inspectorPersonService";
import { getDataVessel } from "@/services/vesselService";

import '../styles/Form.css';

const CancelInspectionProcess = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, process } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [listInspectorPerson, setListInspectorPerson] = useState([]) as any;
    const [listShipName, setListShipName] = useState([]) as any;

    const getDataListInspectorPerson = async (type: string) => {
        const response = await getInspectorPersonData(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorPersonId,
                label: item.inspectorPersonName,
            }));
            setListInspectorPerson(formattedData);
        } else {
            toast({
                title: 'Failed to get data inspector person.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorPerson([]);
        }
    }

    const getListShipName = async (type: string) => {
        const response = await getDataVessel(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        } else {
            setListShipName([]);
        }
    }

    const handleCancel = () => {
        setTitle("Go Back?");
        setConfirmationTest("Are you sure you want to go back?");
        onOpen();
    }

    const handleSubmit = (data: any) => {
        setTitle("Cancel Inspection?");
        setConfirmationTest("Are you sure you want to cancel this inspection?");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const obj = {
            id,
            shipOperatorStatusNotes: dataEdit?.note ?? '',
            name,
            isCancelByAdmin: !process,
        }
        return obj;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Go Back?") {
            //navigate('/inspection-process');
            history.back();
            return;
        }

        const obj = generateData();
        const response = await cancelNomination(obj) as any;
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Cancellation confirmed.',
                description: "We've confirmed your cancellation.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            //navigate('/inspection-process');
            history.back();
        } else {
            toast({
                title: 'Failed to cancel.',
                description: "We unabled to confirm your cancellation.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getNominationById(id) as any;
        if (response) {
            let type = '';

            if (response.data.sire) {
                type = 'sire'
            } else if (response.data.bire) {
                type = 'sire'
            } else if (response.data.vetting) {
                type = 'vetting'
            } else {
                type = ''
            }

            getDataListInspectorPerson(type);
            getListShipName(type);
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText="Yes" cancelButtonText="No" />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                inspectionID: dataDetail?.sequenceID ?? '',
                                inspectingCompany: dataDetail?.companyName ?? '',
                                inspector: dataDetail?.inspectorId ?? '',
                                inspectionDate: dataDetail?.inspectionDate ? format(dataDetail?.inspectionDate, FORMAT_ADD_DATE) : '',
                                shipOperator: dataDetail?.shipOperatorName ?? '',
                                vesselName: dataDetail?.vesselId ?? '',
                                type: dataDetail?.type ?? '',
                                location: dataDetail?.country ?? '',
                                note: dataDetail?.inspectionID ?? ''
                            }}
                            validationSchema={cancelInspectionProcessSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='inspectionID'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ID Inspection</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='ID Inspection' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectingCompany'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspecting Company</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Inspecting Company' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspector'>
                                        {({ form }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspector</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='inspector' handleChange={({ value }: any) => props.setFieldValue('inspector', value)} value={props.values.inspector} defaultValue={props.values.inspector} options={listInspectorPerson} hasError={form.errors.inspector && form.touched.inspector} isDisabled />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectionDate'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspection Date</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Inspection Date' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='shipOperator'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Ship Operator</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Ship Operator' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselName'>
                                        {() => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Name</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectShip name='vesselName' placeholder="Choose Vessel" value={props.values.vesselName} defaultValue={props.values.vesselName} options={listShipName} isDisabled />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='type'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Type' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='location'>
                                        {() => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Location</FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='location' value={props.values.location} defaultValue={props.values.location} isDisabled />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='note'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.note && form.submitCount > 0} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Note<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} placeholder='Note' className="form-input" isInvalid={form.errors.note && form.submitCount > 0} isDisabled={isSubmitting} />
                                                        <FormErrorMessage>{form.errors.note}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Go Back
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Cancel Inspection
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default CancelInspectionProcess;
